/**
 * @internal true
 * @module briefing/BriefingAlerts
 * Widget de alertas/incidentes com ações inline: resolver, compartilhar.
 * Reutiliza lógica de IncidentesPage existente.
 */
import {
  AlertTriangle, Shield, CheckCircle2, Share2, Clock,
  ChevronRight, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface Incidente {
  id: string;
  titulo: string;
  tipoIncidente: string;
  severidade: string;
  status: string;
  descricao: string;
  criadoEm: string;
  aluno?: { id: string; nome: string };
  turma?: { nome: string };
}

interface BriefingAlertsProps {
  incidentes: Incidente[];
  loading: boolean;
  onResolve: (id: string) => Promise<void>;
}

const SEV_ICON_CLASS: Record<string, string> = {
  CRITICA: 'brief-alert-icon--red',
  ALTA: 'brief-alert-icon--orange',
  MEDIA: 'brief-alert-icon--yellow',
  BAIXA: 'brief-alert-icon--blue',
  INFO: 'brief-alert-icon--blue',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

export function BriefingAlerts({ incidentes, loading, onResolve }: BriefingAlertsProps) {
  const navigate = useNavigate();

  const handleShare = (inc: Incidente) => {
    // Dispara evento para abrir GlobalChatWidget com mensagem pré-preenchida
    window.dispatchEvent(
      new CustomEvent('ac:chat:open', {
        detail: {
          message: `🚨 Alerta: ${inc.titulo}\nSeveridade: ${inc.severidade}\nDescrição: ${inc.descricao?.slice(0, 120) || 'Sem descrição'}`,
          preselect: 'operadores',
        },
      }),
    );
    toast.info('Chat aberto para compartilhar o alerta');
  };

  const handleResolve = async (id: string) => {
    try {
      await onResolve(id);
      toast.success('Incidente resolvido com sucesso!');
    } catch {
      toast.error('Erro ao resolver incidente');
    }
  };

  const criticalCount = incidentes.filter((i) => i.severidade === 'CRITICA' || i.severidade === 'ALTA').length;

  return (
    <div className="brief-card">
      <div className="brief-card-head">
        <h3 className="brief-card-title">
          <AlertTriangle style={{ color: '#ef4444' }} />
          Alertas Ativos
          {criticalCount > 0 && (
            <span className="brief-card-badge brief-card-badge--red">{criticalCount}</span>
          )}
        </h3>
        <button
          className="brief-card-link"
          onClick={() => navigate('/equipe/incidentes')}
        >
          Ver todos <ChevronRight size={12} />
        </button>
      </div>
      <div className="brief-card-body">
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="brief-skeleton" style={{ height: 52, borderRadius: 10 }} />
            ))}
          </div>
        ) : incidentes.length === 0 ? (
          <div className="brief-empty">
            <Shield size={28} />
            <span className="brief-empty-text">Tudo tranquilo! Nenhum alerta aberto.</span>
          </div>
        ) : (
          <div className="brief-alert-list">
            {incidentes.slice(0, 8).map((inc) => (
              <div key={inc.id} className="brief-alert-item">
                <div className={`brief-alert-icon ${SEV_ICON_CLASS[inc.severidade] || 'brief-alert-icon--blue'}`}>
                  <AlertTriangle size={14} />
                </div>
                <div className="brief-alert-body">
                  <p className="brief-alert-title">{inc.titulo || inc.tipoIncidente}</p>
                  <p className="brief-alert-sub">
                    {inc.aluno?.nome && <>{inc.aluno.nome} · </>}
                    {inc.turma?.nome && <>{inc.turma.nome} · </>}
                    <Clock size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />
                    {timeAgo(inc.criadoEm)}
                  </p>
                </div>
                <div className="brief-alert-actions">
                  <button
                    className="brief-alert-btn brief-alert-btn--resolve"
                    title="Resolver"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResolve(inc.id);
                    }}
                  >
                    <CheckCircle2 size={14} />
                  </button>
                  <button
                    className="brief-alert-btn brief-alert-btn--share"
                    title="Compartilhar no Chat"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare(inc);
                    }}
                  >
                    <Share2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
